/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-one-expression-per-line */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Tags from '@base/Tags/Tags';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import H2 from '@base/H2/H2';
import H3 from '@base/H3/H3';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import BaseImage from '@base/Image';
import Divider from '@base/Divider/Divider';
import textHelper from '@utils/textHelper';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { CHECKOUT_STARTED } from '@constants/segment';
import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import { Spacings, Fonts } from '@styles/index';
import useCheckoutStartedEvtProps from '@hooks/useCheckoutStartedEvtProps';
import useIconicSubscriptionInfo from '@api/hooks/goal/useIconicSubscriptionInfo';
import usePartPaymentStatus from '@api/hooks/payments/usePartPaymentStatus';
import MobileIconicSection from './MobileIconicSection';

const Heading = styled(H2)`
  margin: ${Spacings.SPACING_2B} 0;
  @media (max-width: 70.95em) {
    font-size: 22px;
    margin-bottom: 4px;
  }
`;

const SubHeading = styled(H5)`
  font-weight: ${Fonts.REGULAR};
`;

const MobileSectionWrapper = styled.div`
  margin-top: 16px;
  @media (min-width: 47.95em) {
    display: none;
  }
`;

const SecWrapper = styled.section`
  width: 100%;
  position: relative;
  margin-bottom: ${Spacings.SPACING_30B};
  @media (max-width: 70.95em) {
    margin-bottom: 56px;
  }
  @media (max-width: 47.95em) {
    display: none;
  }
`;

const Container = styled.div`
  background-color: var(--color-i-white);
  box-shadow: ${boxShadowGenerator()};
  border-radius: 8px;
  position: relative;
  top: -40px;
  text-align: center;
  width: 1136px;
  margin: auto;
  padding-top: ${Spacings.SPACING_20B};
  @media (max-width: 70.95em) {
    width: 728px;
    padding-top: 32px;
  }
  @media (max-width: 47.95em) {
    top: 0;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  margin-left: 96px;
  margin-right: 96px;
  margin-top: 74px;
  flex-wrap: wrap;
  @media (min-width: 70.95em) {
    & > :nth-child(even) {
      margin-left: 96px;
    }
  }
  @media (max-width: 70.95em) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 78px;
    grid-row-gap: 32px;
    margin: 40px 58px 32px 61px;
  }
`;

const CardWrapper = styled.div`
  width: 416px;
  margin-bottom: 66px;
  text-align: left;
  display: flex;
  @media (max-width: 70.95em) {
    width: 100%;
    margin-bottom: 0px;
  }
`;

const CardContentLeft = styled.div`
  margin-right: ${Spacings.SPACING_4B};
`;

const CardContentRight = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 150%;
  color: var(--color-i-black);
  margin-top: 16px;
  @media (max-width: 70.95em) {
    font-size: 14px;
    margin-top: 8px;
  }
`;

const Image = styled(BaseImage)`
  @media (max-width: 70.95em) {
    width: 48px;
    height: 48px;
    object-fit: contain;
  }
`;

const CardTitle = styled(H3)`
  @media (max-width: 70.95em) {
    font-size: 16px;
    line-height: 150%;
  }
`;

const Card = ({ card }) => (
  <CardWrapper>
    <CardContentLeft>
      <Image width={80} height={34} src={card.benefitLogo} alt="Benefit" />
    </CardContentLeft>
    <CardContentRight>
      <CardTitle>{card.benefitHeader}</CardTitle>
      <Description>{card.benefitDesc}</Description>
    </CardContentRight>
  </CardWrapper>
);

const ExamPricing = styled.span`
  color: var(--color-i-blue);
  padding-left: 5px;
  @media (max-width: 70.95em) {
    font-size: 22px;
    display: inline-block;
  }
`;

const PrciningVal = styled.div`
  width: 600px;
  text-align: left;
  @media (max-width: 70.95em) {
    width: auto;
  }
`;

const Pricing = styled.div`
  padding: 64px 96px;
  display: flex;
  justify-content: space-between;
  @media (max-width: 70.95em) {
    padding: 32px 58px 32px 61px;
  }
`;

const LinkContainer = styled.div`
  align-self: center;
`;

const UNDivider = styled(Divider)`
  margin: 0 96px;
  @media (max-width: 70.95em) {
    height: 2px;
    margin: 0 58px 0 61px;
  }
`;

const PricingTitle = styled(H3)`
  @media (max-width: 70.95em) {
    font-size: 18px;
    display: inline-block;
  }
`;

const ViewPricing = styled(H4)`
  @media (max-width: 70.95em) {
    font-size: 16px;
    line-height: 100%;
  }
`;

const GeneralIconicSection = ({
  block,
  goalUID,
  goalSlug,
  countryCode,
  goalDetails,
  dataAnalytics
}) => {
  const { extraBlockInfo } = block;
  const { iconicSubscriptionInfo } = useIconicSubscriptionInfo(
    `iconic-${goalUID}`
  );

  const { partPaymentStatus } = usePartPaymentStatus(goalUID);
  const eventProps = useCheckoutStartedEvtProps(goalDetails);
  const goalCurrencyCode = textHelper.currencySymbolMap(countryCode);
  const handleAnalytics = () => {
    segment.track(CHECKOUT_STARTED, eventProps, evtNameCategoryMap.BOTH);
  };
  const subHeading =
    iconicSubscriptionInfo?.desc ||
    `Take your ${extraBlockInfo?.goalName} preparation to next level with Personal Coach`;
  return (
    <>
      <MobileSectionWrapper>
        <MobileIconicSection
          type="Iconic"
          info={{
            heading: 'ICONIC',
            subHeading,
            data: iconicSubscriptionInfo?.benefits
          }}
        />
      </MobileSectionWrapper>

      <SecWrapper hasPartPayment={partPaymentStatus?.isActivePartSet}>
        <Container>
          <Tags type="filled" bgColor="var(--color-i-orange-1)">
            Introducing
          </Tags>
          <Heading>ICONIC subscription</Heading>
          <SubHeading>{subHeading}</SubHeading>
          <CardsContainer>
            {iconicSubscriptionInfo?.benefits?.map((card) => (
              <Card card={card} key={card.id} />
            ))}
          </CardsContainer>
          {!partPaymentStatus?.isActivePartSet && (
            <>
              <UNDivider />
              <Pricing>
                <PrciningVal>
                  <PricingTitle>
                    {`${extraBlockInfo?.goalName} - ICONIC subscription starts `}
                    <br /> from
                    <ExamPricing>
                      {`${goalCurrencyCode}${textHelper.commas(
                        Math.round(extraBlockInfo?.lowestPrice)
                      )}`}
                      /month
                    </ExamPricing>
                  </PricingTitle>
                </PrciningVal>
                <LinkContainer>
                  <UnderlineLink
                    color="var(--color-i-green)"
                    href="/goal/[goalSlug]/[goalUID]/subscribe"
                    as={`/goal/${goalSlug}/${goalUID}/subscribe`}
                    onClick={handleAnalytics}
                  >
                    <ViewPricing
                      color="var(--color-i-green)"
                      data-analytics={`${dataAnalytics}-viewPricing`}
                    >
                      View pricing
                    </ViewPricing>
                  </UnderlineLink>
                </LinkContainer>
              </Pricing>
            </>
          )}
        </Container>
      </SecWrapper>
    </>
  );
};

GeneralIconicSection.propTypes = {
  block: PropTypes.objectOf(PropTypes.any).isRequired,
  goalDetails: PropTypes.objectOf(PropTypes.any),
  goalUID: PropTypes.string.isRequired,
  goalSlug: PropTypes.string.isRequired,
  dataAnalytics: PropTypes.string
};

GeneralIconicSection.defaultProps = {
  dataAnalytics: '',
  goalDetails: {}
};

export default GeneralIconicSection;
